import { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type {
  ConversationPriority,
  ConversationStatus,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import { useHandler } from "@redotech/react-util/hook";
import { useGlobalConversationCollections } from "../providers/global-conversation-collections-context";

/**
 * Returns a function to update a conversation's status.
 */
export function useUpdateConversationStatus(): (
  conversationId: ConversationId,
  status: ConversationStatus,
) => void {
  const collections = useGlobalConversationCollections();

  return useHandler(
    (conversationId: ConversationId, status: ConversationStatus) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.status = status;
      });
    },
  );
}

/**
 * Returns a function to update a conversation's priority.
 */
export function useUpdateConversationPriority(): (
  conversationId: ConversationId,
  priority: ConversationPriority | null,
) => void {
  const collections = useGlobalConversationCollections();

  return useHandler(
    (conversationId: ConversationId, priority: ConversationPriority | null) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.priority = priority;
      });
    },
  );
}

/**
 * Returns a function to mark a conversation as read.
 */
export function useMarkConversationRead(): (
  conversationId: ConversationId,
) => void {
  const collections = useGlobalConversationCollections();

  return useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.readAt = new Date();
    });
  });
}

/**
 * Returns a function to mark a conversation as unread.
 */
export function useMarkConversationUnread(): (
  conversationId: ConversationId,
) => void {
  const collections = useGlobalConversationCollections();

  return useHandler((conversationId: ConversationId) => {
    collections.conversationsList.update(conversationId, (draft) => {
      draft.readAt = null;
    });
  });
}

/**
 * Returns a function to update a conversation's assignee with optimistic updates.
 * Pass null to unassign.
 */
export function useUpdateConversationAssignee(): (
  conversationId: ConversationId,
  assigneeId: MongoUserId | null,
) => void {
  const collections = useGlobalConversationCollections();

  return useHandler(
    (conversationId: ConversationId, assigneeId: MongoUserId | null) => {
      collections.conversationsList.update(conversationId, (draft) => {
        draft.mongoAssigneeId = assigneeId;
      });
    },
  );
}
